/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import type { ShopperSearch } from '@/scapi';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import type { RefinementProps } from '../types';

/**
 * NM's colour facet group — live's ROW STRUCTURE, deliberately WITHOUT the
 * colour disc.
 *
 * WHAT LIVE PAINTS, and it is measured rather than inferred. Live NM at 1728
 * CSS, three filters applied, in the owner capture
 * `live-shots/owner-2026-08-23-filters/filters-5-49-14PM.png` (provenance read
 * from its own address bar):
 *
 *   - a FILLED DISC per colour value
 *   - ONE value per row
 *   - the selected row carries a ring on its disc and a BOLD label
 *   - NO hit count on any colour row
 *
 * Three of those four are reproduced here. The disc is not, and the ring is not
 * — a ring is a property OF the disc, so it cannot outlive it.
 *
 * WHY THERE IS NO DISC: THE CATALOGUE CARRIES NO COLOUR. This is a data limit,
 * not a styling gap, and both ways of resolving it were probed against the live
 * bkcb-004 instance on 2026-08-23 before this renderer was written.
 *
 *   ROUTE 1 — a presentation value on the variation-attribute definition.
 *   REFUTED. A `color` variation-attribute value comes back carrying exactly
 *   `name`, `value` and `orderable`. There is no `presentationId`, no hex, no
 *   image. Sampled over the products behind five NM queries; every one had the
 *   same three keys. The refinement value itself is thinner still — the wire
 *   shape is `{ hitCount, label, value }` and nothing else.
 *
 *   ROUTE 2 — join the refinement value to a variant that carries a swatch.
 *   REFUTED, and structurally rather than incidentally. The refinement is
 *   `c_color`, whose values are BUCKETS — `Black`, `Red`, `White`, `Blue`,
 *   `Neutrals`, `Orange`, `Brown`, `Pink`, `Grey` across both brands. A product
 *   belongs to a bucket per VARIANT, so the join is many-to-many and carries no
 *   field naming which variant earned which bucket. Measured over ten queries
 *   times nine buckets: 4 of 20 NM products in a colour bucket sit in more than
 *   one, and product `0400001561441` sits in FIVE at once — Black, Red,
 *   Neutrals, Orange, Brown — with eleven colour variants including `RED`,
 *   `TAN`, `BROWN`, `NAVY BLUE`, `BLACK` and `Navy Blue and Orange`. Picking one
 *   of those eleven to stand for `Neutrals` is a guess with no field behind it.
 *   Search hits carry no colour or swatch key at all, so there is no shortcut.
 *
 * `product-tile/swatches.tsx` is not a counter-example. Its discs come from
 * per-product variation `swatch` / `swatchSource` images derived in
 * `lib/product/color-swatch-image-seed.ts` — a per-VARIANT asset. A refinement
 * bucket is not a variant and has no such asset.
 *
 * A NAME-TO-COLOUR MAP IS THE ONE THING NOT TO DO HERE. Eight of the nine
 * buckets are literal colour words and would map trivially; `Neutrals` is not,
 * and a disc invented for it is wrong in the one way nobody can see — a shopper
 * cannot tell a guessed swatch from a real one, so the error is silent and
 * permanent. That is why this degrades to a plain row instead: a row promises
 * nothing, and it is strictly closer to live than the counted checkbox list it
 * replaces.
 *
 * THE CHECKBOX STAYS, and it is the reason this is not simply live's markup.
 * Live's disc IS the control — there is no checkbox on a live colour row. With
 * no disc there would be no affordance at all, so the checkbox remains as the
 * only thing a shopper can aim at. It is the one element here live does not
 * have, and it goes the moment a real colour source lands.
 *
 * WHAT UNBLOCKS THE DISC: an `image-group view-type="swatch"` per colour on the
 * catalogue, or any per-bucket presentation attribute SCAPI will return. Either
 * makes the disc real data; neither can be faked from what is on the wire today.
 *
 * NOT CAPPED, unlike the default renderer. `refine-default` bounds its list with
 * `--facet-value-list-max-height` and records that the price range control and
 * the colour group stay uncapped because that is what live does. This honours
 * that rather than re-opening it.
 */
export default function ColorRefinement({
    values,
    attributeId,
    isFilterSelected,
    toggleFilter,
}: RefinementProps): ReactElement {
    return (
        <div className="space-y-1 mt-2" data-testid="refinement-color-list">
            {values.map((value: ShopperSearch.schemas['ProductSearchRefinementValue'], idx) => {
                const id = `refine-${attributeId}-${idx}`;
                const isSelected = isFilterSelected(attributeId, value.value);

                return (
                    <label
                        key={`${attributeId}:${value.value}`}
                        htmlFor={id}
                        data-testid="refinement-color-row"
                        className="flex items-center p-2 hover:bg-muted/30 cursor-pointer">
                        <Checkbox
                            id={id}
                            checked={isSelected}
                            onCheckedChange={() => toggleFilter(attributeId, value.value)}
                            className="size-4"
                        />
                        {/* NO HIT COUNT, and its absence is the measured half of
                            this row. Live NM's Color group shows `Black` and
                            `Pink` with no parenthesised figure on either, while
                            the same rail's Category group does carry one — so
                            this is specific to colour rather than a rail-wide
                            rule, and `refine-default` keeps its count for every
                            other group.

                            The selected label is BOLD, which live uses as the
                            selected-state signal alongside the ring on its disc.
                            The ring has no home here, so the weight carries the
                            state on its own. The exact numeric weight live sets
                            is `unmeasured` — no NM stylesheet for this surface is
                            on disk and a weight cannot be read off a PNG — so
                            this takes the type scale's own semibold step rather
                            than inventing a number. */}
                        <span className={cn('type-label-medium ml-3', isSelected && 'font-semibold')}>
                            {value.label || value.value}
                        </span>
                    </label>
                );
            })}
        </div>
    );
}
